import { Box, Input, InputProps } from '@chakra-ui/react';
import React, { useCallback, useRef, useState } from 'react';
import { FaRegClock } from 'react-icons/fa6';
import PlaceholderOverlay from '../Shared/PlaceholderOverlay';
import IconOverlay from '../Shared/IconOverlay';
import { isAndroid, isFirefox, isSafari } from 'react-device-detect';

export type TimeInputProps = {
	value?: string;
	onChange?: (props: {
		value: string;
		event: React.ChangeEvent<HTMLInputElement>;
	}) => void;
	minTime?: string;
	maxTime?: string;
	invalidField?: string;
} & Omit<InputProps, 'value' | 'onChange'>;

export const TimeInput = React.forwardRef<HTMLInputElement, TimeInputProps>(
	({ name, value, onChange, minTime, maxTime, ...rest }, ref) => {
		const [inputValue, setInputValue] = useState(value ?? '');
		const inputRef = useRef<HTMLInputElement | null>(null);

		React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

		const handleOverlayClick = (e: React.MouseEvent<HTMLInputElement>) => {
			const input = e.currentTarget.parentElement
				?.lastChild as HTMLInputElement | null;
			if (input) {
				// Força o valor para garantir que o onChange será disparado no Safari/iOS
				e.currentTarget.style.display = 'none';
				input.defaultValue = '00:00';

				input.focus();
			}
		};

		const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
			const input = e.target as HTMLInputElement | null;
			if (input?.value && isSafari) {
				setInputValue(input.value);
			}
			e.currentTarget.showPicker?.();
		};

		const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
			if (e.target.value === '' || !value) {
				setInputValue('');
			}

			const input = e.target as HTMLInputElement | null;
			if (input?.value) {
				onChange?.({
					value: input.value,
					event: {
						...({} as React.ChangeEvent<HTMLInputElement>),
						target: input,
					},
				});
			}
		};

		const handleChange = useCallback(
			(event: React.ChangeEvent<HTMLInputElement>) => {
				onChange?.({ value: event.target.value, event });
				setInputValue(event.target.value);
			},
			[onChange]
		);

		React.useEffect(() => {
			setInputValue(value ?? '');
		}, [value]);

		return (
			<>
				<Box position="relative" flex={1} h="51px" bg="white">
					{value === '' && (
						<PlaceholderOverlay
							label="Selecione a hora"
							onClick={handleOverlayClick}
						/>
					)}
					<IconOverlay
						icon={
							<FaRegClock
								size={isFirefox && isAndroid ? '0.8rem' : '1rem'}
								z={10}
							/>
						}
						left={isFirefox ? '' : '13px'}
						right={'18px'}
					/>
					<Input
						ref={inputRef}
						name={`${name}-time`}
						h="full"
						title="Hora"
						type="time"
						defaultValue="00:00"
						value={inputValue ?? ''}
						color="#000"
						onFocus={handleFocus}
						onChange={handleChange}
						onBlurCapture={handleBlur}
						flex={1}
						bg="transparent"
						zIndex="2"
						cursor="default"
						fontSize={{ base: '12px', sm: 'inherit' }}
						pl={isFirefox ? '15px' : '37px'}
						border="1px solid #666"
						_focus={{ border: '1px solid #666' }}
						_hover={{ border: '1px solid #666' }}
						_invalid={{ border: '2px solid red' }}
						_placeholder={{
							color: { base: '#222', lg: '#666' },
						}}
						autoComplete="off"
						{...rest}
					/>
				</Box>
			</>
		);
	}
);

TimeInput.displayName = 'TimeInput';

export default TimeInput;
